import { z } from 'zod';
import { CalendarDate } from './project.js';
/**
 * Hoạt động trong ngày của đội — APM-DAY, docs/02-sprint-plan.md "# Hoạt động
 * trong ngày của đội". Director (quyền hiệu suất không điều kiện) xem cả tổ
 * chức, Leader xem cấp dưới trực tiếp.
 *
 * Tất cả là số **dẫn xuất** lúc đọc từ `work_session`, `task.completed_at` và
 * `event_log` — không bảng riêng, không ảnh chụp.
 */
/** Trần người trong một bảng — quá thì 400, không cắt im lặng một con người. */
export const TEAM_DAILY_MAX_MEMBERS = 300;
/** Cửa sổ gộp sổ `sensitive_access_log` của màn hình tự làm mới mỗi phút (người dùng chốt 2026-09-28). */
export const TEAM_DAILY_AUDIT_WINDOW_MS = 30 * 60_000;
export const TeamDailyQuery = z.object({
    /** Vắng = hôm nay theo múi giờ tổ chức. */
    date: CalendarDate.optional(),
});
/** `running` = đang bấm giờ; `paused` = đoạn cuối trong ngày dừng tạm/hết ca; `idle` = không. */
export const TeamDailyState = z.enum(['running', 'paused', 'idle']);
export const TeamDailyItemKind = z.enum(['task', 'issue']);
/** Một task/issue người đó đã đụng tới trong ngày. */
export const TeamDailyItem = z.object({
    kind: TeamDailyItemKind,
    id: z.string(),
    key: z.string(),
    title: z.string(),
    projectId: z.string(),
    projectName: z.string(),
    /** Cột hiện tại — `null` với issue. */
    statusName: z.string().nullable(),
    /** Giờ bấm trong ngày, cắt theo ngày; đoạn đang mở tính tới lúc đọc. */
    hours: z.number().min(0),
    /** Đang bấm giờ trên hạng mục này (chỉ hôm nay). */
    running: z.boolean(),
    /** Task xong trong ngày và được quy cho người này. */
    done: z.boolean(),
    /** Tiến độ đầu ngày → cuối ngày (hôm nay: tới lúc đọc). `null` = không dựng lại được / issue. */
    progressFrom: z.number().min(0).max(100).nullable(),
    progressTo: z.number().min(0).max(100).nullable(),
    /** Chữ các mục checklist **người này** tick xong trong ngày. */
    checklistDone: z.array(z.string()),
});
export const TeamDailyMember = z.object({
    userId: z.string(),
    name: z.string(),
    /** `null` với ngày đã qua — "đang làm" chỉ có nghĩa ở hiện tại. */
    state: TeamDailyState.nullable(),
    /** Việc đang bấm giờ (hoặc vừa tạm dừng) — chỉ hôm nay. */
    current: z
        .object({
        kind: TeamDailyItemKind,
        id: z.string(),
        key: z.string(),
        title: z.string(),
        projectName: z.string(),
        /** Đang chạy: lúc bắt đầu đoạn; tạm dừng: lúc dừng. ISO. */
        since: z.string(),
    })
        .nullable(),
    /** Tổng thời gian làm việc trong ngày — giờ bấm, cắt theo ngày. */
    hours: z.number().min(0),
    /**
     * Phần của `hours` nằm **ngoài ca** theo lịch làm việc của chính người đó
     * (ngày nghỉ, ngày lễ ⇒ mọi phút) — cùng phép tính với "Làm thêm" của
     * timesheet (`overtimeMinutesWithin`). Luôn ≤ `hours`.
     */
    overtimeHours: z.number().min(0),
    /** Đang bấm giờ **ngoài ca** lúc đọc — web cộng dồn cả OT theo đồng hồ, như `hours`. */
    overtimeRunning: z.boolean(),
    tasksDone: z.number().int().min(0),
    checklistDone: z.number().int().min(0),
    items: z.array(TeamDailyItem),
});
export const TeamDailyActivity = z.object({
    date: CalendarDate,
    /** Hôm nay theo múi giờ tổ chức — web khoá nút `>` ở đây. */
    today: CalendarDate,
    timezone: z.string(),
    /** Lúc đọc (ISO) — web cộng dồn giờ của người đang bấm giờ từ mốc này. */
    capturedAt: z.string(),
    /** `org` = cả tổ chức (Director), `direct` = cấp dưới trực tiếp. */
    scope: z.enum(['org', 'direct']),
    members: z.array(TeamDailyMember).max(TEAM_DAILY_MAX_MEMBERS),
});
//# sourceMappingURL=team-daily.js.map